import React from 'react';
import { useParams } from 'react-router-dom';
import { ConversionForm } from '../components/ConversionForm';
import { UnitCategory } from '../types/units';
import { SEO } from '../components/SEO';
import { getUnitByValue, unitsByCategory } from '../utils/units';
import { Helmet } from 'react-helmet-async';
import { popularConversions } from '../data/popularConversions';

// Map URL shortcuts to actual unit values
const urlAliasMap: Record<string, string> = {
  // Length
  'km': 'kilometer',
  'kilometers': 'kilometer',
  'meter': 'meter',
  'meters': 'meter',
  'm': 'meter',
  'cm': 'centimeter',
  'centimeters': 'centimeter',
  'mm': 'millimeter',
  'millimeters': 'millimeter',
  'mile': 'mile',
  'miles': 'mile',
  'mi': 'mile',
  'ft': 'foot',
  'feet': 'foot',
  'in': 'inch',
  'inches': 'inch',
  'yd': 'yard',
  'yards': 'yard',
  
  // Weight
  'kg': 'kg',
  'kilograms': 'kg',
  'g': 'g',
  'grams': 'g',
  'mg': 'mg',
  'milligrams': 'mg',
  'ug': 'ug',
  'micrograms': 'ug',
  'lb': 'lb',
  'lbs': 'lb',
  'pounds': 'lb',
  'oz': 'oz',
  'ounces': 'oz',
  'st': 'st',
  'stone': 'st',
  'ton': 'ton_us',
  'tons': 'ton_us',
  
  // Temperature
  'c': 'celsius',
  'celsius': 'celsius',
  'centigrade': 'celsius',
  'f': 'fahrenheit',
  'fahrenheit': 'fahrenheit',
  'k': 'kelvin',
  'kelvin': 'kelvin',
  'r': 'rankine',
  'rankine': 'rankine',
  're': 'reaumur',
  'reaumur': 'reaumur',
  'de': 'delisle',
  'delisle': 'delisle',
  
  // Cooking
  'cup': 'cup',
  'cups': 'cup',
  'tbsp': 'tbsp',
  'tablespoon': 'tbsp',
  'tablespoons': 'tbsp',
  'tsp': 'tsp',
  'teaspoon': 'tsp',
  'teaspoons': 'tsp',
  
  'ml' : 'ml',
  'milliliter': 'ml',
  'milliliters': 'ml',
  'ul': 'microliter',
  'microliter': 'ul',
  'microliters': 'ul',
  'l': 'liter',
  'liter': 'l',
  'liters': 'l',
  'gal': 'gallon',
  'gallon': 'gal',
  'gallons': 'gal',
  'qt': 'quart',
  'quart': 'qt',
  'quarts': 'qt',
  'pt': 'pint',
  'pint': 'pt',
  'pints': 'pt',
  'floz': 'floz',
  'fl-oz': 'floz',
  'fluid-ounce': 'fl_oz',
  'fluid-ounces': 'fl_oz',
  
  // Time
  'year': 'y',
  'years': 'y',
  'y': 'year',
  'month': 'mo',
  'months': 'mo',
  'mo': 'month',
  'week': 'w',
  'weeks': 'w',
  'w': 'week',
  'day': 'd',
  'days': 'd',
  'd': 'day',
  'hour': 'h',
  'hours': 'h',
  'h': 'hour',
  'minute': 'min',
  'minutes': 'min',
  'min': 'minute',
  'second': 's',
  'seconds': 's',
  's': 'second',
  'millisecond': 'ms',
  'milliseconds': 'ms',
  'ms': 'millisecond',
  
  // Data Storage
  'byte': 'byte',
  'bytes': 'byte',
  'bit': 'bit',
  'bits': 'bit',
  'kb': 'kb',
  'kilobyte': 'kb',
  'kilobytes': 'kb',
  'mb': 'mb',
  'megabyte': 'mb',
  'megabytes': 'mb',
  'gb': 'gb',
  'gigabyte': 'gb',
  'gigabytes': 'gb',
  'tb': 'tb',
  'terabyte': 'tb',
  'terabytes': 'tb',
  
  // Energy
  'j': 'joule',
  'joule': 'j',
  'joules': 'j',
  'cal': 'calorie',
  'calorie': 'cal',
  'calories': 'cal',
  'kcal': 'kcal',
  'kilocalorie': 'kcal',
  'kilocalories': 'kcal',
  'kwh': 'kwh',
  'kilowatt-hour': 'kwh',
  'btu': 'btu',
  'ft-lb': 'ft_lb',
  'foot-pound': 'ft_lb',
  
  // Pressure
  'pa': 'pa',
  'pascal': 'pa',
  'kpa': 'kpa',
  'kilopascal': 'kpa',
  'psi': 'psi',
  'bar': 'bar',
  'atm': 'atm',
  'atmosphere': 'atm',
  'mmhg': 'mmhg',
  'torr': 'torr',
  'inhg': 'inhg',
  
  // Speed
  'mph': 'mph',
  'miles-per-hour': 'mph',
  'kph': 'kph',
  'kmh': 'kph',
  'kilometers-per-hour': 'kph',
  'mps': 'mps',
  'meters-per-second': 'mps',
  'fps': 'fps',
  'feet-per-second': 'fps',
  'knot': 'knot',
  'knots': 'knot',
  'mach': 'mach',
  
  // Angle
  'deg': 'degree',
  'degree': 'deg',
  'degrees': 'deg',
  'rad': 'radian',
  'radian': 'rad',
  'radians': 'rad',
  'grad': 'gradian',
  'gradian': 'grad',
  'gradians': 'grad',
  'mil': 'mil',
  'mils': 'mil',
  'arcmin': 'minute_arc',
  'arcminute': 'minute_arc',
  'arcsec': 'second_arc',
  'arcsecond': 'second_arc',
  
  // Frequency
  'hz': 'hz',
  'hertz': 'hz',
  'khz': 'khz',
  'kilohertz': 'khz',
  'mhz': 'mhz',
  'megahertz': 'mhz',
  'ghz': 'ghz',
  'gigahertz': 'ghz',
  'rpm': 'rpm',
  'period': 'period_s',
  'period-s': 'period_s',
  'period-seconds': 'period_s'
};

export default function DirectConversionPage() {
  const { category = 'length', conversion } = useParams<{ category: string; conversion: string }>();
  
  console.log('URL params:', { category, conversion });
  
  // Parse the conversion parameter (e.g., "km-to-miles")
  const [fromAlias, toAlias] = (conversion || '').split('-to-');
  console.log('Parsed aliases:', { fromAlias, toAlias });
  
  // Convert URL aliases to actual unit values
  const fromUnit = urlAliasMap[fromAlias] || fromAlias;
  const toUnit = urlAliasMap[toAlias] || toAlias;
  console.log('Mapped units:', { fromUnit, toUnit });
  
  // Verify that the units exist in the category
  const availableUnits = unitsByCategory[category as UnitCategory] || [];
  console.log('Available units:', availableUnits.map(u => u.value));
  console.log('Looking for fromUnit:', fromUnit);
  console.log('Looking for toUnit:', toUnit);
  
  const validFromUnit = availableUnits.find(u => u.value === fromUnit)?.value || availableUnits[0]?.value;
  const validToUnit = availableUnits.find(u => u.value === toUnit)?.value || availableUnits[1]?.value;
  
  console.log('Valid from unit:', validFromUnit);
  console.log('Valid to unit:', validToUnit);
  
  // Get readable unit names for SEO
  const fromUnitLabel = getUnitByValue(category as UnitCategory, validFromUnit)?.label || fromUnit;
  const toUnitLabel = getUnitByValue(category as UnitCategory, validToUnit)?.label || toUnit;
  
  // Find the conversion details from popularConversions
  const findConversionDetails = () => {
    if (!category || !conversion) return null;
    
    const categoryConversions = popularConversions[category as UnitCategory];
    if (!categoryConversions) return null;
    
    return categoryConversions.find(conv => conv.url === conversion);
  };

  const conversionDetails = findConversionDetails();

  if (!conversionDetails) {
    return <div>Conversion not found</div>;
  }

  const { from, to } = conversionDetails;

  return (
    <div className="container mx-auto px-4 py-8">
      <Helmet>
        <title>Convert {from} to {to} | {category} Converter</title>
        <meta name="description" content={`Convert ${from} to ${to} with our easy to use ${category} converter. Get instant, accurate conversions.`} />
      </Helmet>
      <SEO 
        title={`Convert ${fromUnitLabel} to ${toUnitLabel} (${category})`}
        description={`Convert ${category} units from ${fromUnitLabel} to ${toUnitLabel} with our easy-to-use converter. Get instant results and step-by-step explanations.`}
      />
      <div className="max-w-4xl mx-auto p-4">
        <h1 className="text-2xl font-bold mb-4 capitalize">
          Convert {from} to {to}
        </h1>
        <ConversionForm 
          category={category as UnitCategory}
          defaultFromUnit={from}
          defaultToUnit={to}
        />
        <div className="mt-8 prose">
          <h2>About {from} to {to} Conversion</h2>
          <p>
            Use our converter above to easily convert from {from} to {to}. 
            This {category} converter provides instant, accurate results with 
            detailed conversion steps.
          </p>
        </div>
      </div>
    </div>
  );
} 